import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { AssetWizard } from "@/features/assets/AssetWizard";
import { useAsset } from "@/features/assets/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canEditAsset } from "@/lib/roles";
import { isDisposed, DISPOSED_ACTION_TOAST } from "@/features/assets/lifecycle";

export const Route = createFileRoute("/_authenticated/assets/$assetId/edit")({
  component: EditAssetPage,
});

function EditAssetPage() {
  const { assetId } = Route.useParams();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { data, isLoading } = useAsset(assetId);

  useEffect(() => {
    if (data && isDisposed(data)) {
      toast.error(DISPOSED_ACTION_TOAST);
      navigate({ to: "/assets/$assetId", params: { assetId }, replace: true });
    }
  }, [data, assetId, navigate]);

  useEffect(() => {
    if (me && !canEditAsset(me.roles)) {
      if (me.roles.includes("finance")) {
        toast.info("Finance users can view assets but cannot edit asset master details.");
        navigate({ to: "/assets/$assetId", params: { assetId }, replace: true });
      }
    }
  }, [me, assetId, navigate]);

  if (!me) return null;
  if (!canEditAsset(me.roles)) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border bg-card p-6">
        <h1 className="text-lg font-semibold">Permission required</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You do not have permission to edit assets. Please contact the
          Biomedical / Admin team.
        </p>
      </div>
    );
  }

  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return <div className="py-20 text-center text-muted-foreground">Asset not found.</div>;
  if (isDisposed(data)) {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  return <AssetWizard mode="edit" initial={data} />;
}
